import Downshift, { GetInputPropsOptions } from 'downshift';
import { map } from 'lodash';
import React from 'react';
import styled from 'styled-components/macro';
import { getFilteredOrderedOptions } from '../helpers/searchHelpers';
import { colourNeutral09 } from '../styling/colours';
import { fontFamily, fontSize03 } from '../styling/fonts';
import { spacing01, spacing02, spacing11 } from '../styling/spacing';
import { ValidityProps } from './form/FormField';
import { inputBorderWidth } from './form/inputs/inputStyles';
import { TextInput } from './TextInput';

export const autocompleteInputTestId = 'auto-complete-input';

export type AutocompleteItem = { id: string; displayText: string };

type Props<TItem extends AutocompleteItem> = {
  onChange: (newValue: string) => void;
  onBlur?: () => void;
  autocompleteItems: Array<TItem>;
  itemToString?: (item: TItem | null) => string;
  value: string;
  name?: string;
  onSelect: (selectedItem: TItem | null) => void;
  disabled?: boolean;
} & ValidityProps;

export const AutocompleteTextInput = <TItem extends AutocompleteItem>({
  onChange,
  onBlur,
  onSelect,
  autocompleteItems,
  itemToString = item => (item ? item.displayText : ''),
  value,
  name,
  $isValid,
  $isInvalid,
  disabled,
}: Props<TItem>) => {
  const onBlurWrapper = (inputProps: GetInputPropsOptions) => {
    return (event: React.FocusEvent<HTMLInputElement>) => {
      if (inputProps.onBlur != null) {
        inputProps.onBlur(event);
      }
      if (onBlur != null) {
        onBlur();
      }
    };
  };

  const onChangeWrapper = (inputProps: GetInputPropsOptions) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      if (inputProps.onChange != null) {
        inputProps.onChange(event);
      }
      onChange(event.target.value);
    };
  };

  return (
    <Downshift inputValue={value} onSelect={onSelect} itemToString={itemToString}>
      {({ getInputProps, getItemProps, highlightedIndex, isOpen, inputValue }) => {
        const inputProps = getInputProps();

        return (
          <div>
            <Container>
              <TextInput
                {...getInputProps()}
                // overriding Downshift behaviour for Home and End
                onKeyDown={event => {
                  if (!(event.key === 'Home' || event.key === 'End')) {
                    if (inputProps.onKeyDown) {
                      inputProps.onKeyDown(event);
                    }
                  }
                }}
                onChange={onChangeWrapper(inputProps)}
                onBlur={onBlurWrapper(inputProps)}
                $isValid={$isValid}
                $isInvalid={$isInvalid}
                data-testid={autocompleteInputTestId}
                name={name}
                disabled={disabled}
              />
              {isOpen ? (
                <AutocompleteMenu>
                  {map(
                    getFilteredOrderedOptions(inputValue, autocompleteItems, item => item.displayText),
                    (item, index) => (
                      <Item
                        key={item.id}
                        {...getItemProps({ item, index })}
                        $isHighlighted={highlightedIndex === index}
                      >
                        {itemToString(item)}
                      </Item>
                    ),
                  )}
                </AutocompleteMenu>
              ) : null}
            </Container>
          </div>
        );
      }}
    </Downshift>
  );
};

const AutocompleteMenu = styled.div`
  overflow-y: auto;
  max-height: ${spacing11};
  position: absolute;
  width: calc(100% - 2 * ${inputBorderWidth});
  z-index: 1;
  text-overflow: ellipsis;
  border: solid ${colourNeutral09};
  border-width: 0 ${inputBorderWidth} ${inputBorderWidth} ${inputBorderWidth};
`;

const Container = styled.div`
  width: 100%;
`;

const Item = styled.div<{ $isHighlighted: boolean }>`
  font-family: ${fontFamily};
  padding: ${spacing01} ${spacing02};
  font-size: ${fontSize03};
  background-color: ${props => (props.$isHighlighted ? colourNeutral09 : 'white')};
`;
